import React, { useState, useEffect, useRef } from 'react';
import { X, BookOpen, Search, Quote, RefreshCw, ExternalLink, ChevronLeft, ChevronRight, AlertCircle, ChevronDown } from 'lucide-react';
import {
  COMMENTARY_SOURCES,
  CommentarySourceId,
  CommentaryVerse,
  ParsedPassage,
  parsePassage,
  isNTBook,
} from '../lib/commentary';
import { useCommentary } from '../hooks/useCommentary';

interface CommentaryLibraryProps {
  isOpen: boolean;
  onClose: () => void;
  sermonScripture: string;
  onInsertQuote: (text: string, sourceName: string, reference: string) => void;
  embedded?: boolean;
}

// ─── Skeleton Loader ──────────────────────────────────────────────────────────

function SkeletonLoader() {
  return (
    <div className="p-6 space-y-7 animate-pulse">
      {[0, 1, 2].map(i => (
        <div key={i} className="space-y-2">
          <div className="h-3 w-14 bg-gold/20 rounded" />
          <div className="h-3.5 bg-gray-200 dark:bg-white/10 rounded w-full" />
          <div className="h-3.5 bg-gray-200 dark:bg-white/10 rounded w-5/6" />
          <div className="h-3.5 bg-gray-200 dark:bg-white/10 rounded w-4/5" />
          <div className="h-3.5 bg-gray-200 dark:bg-white/10 rounded w-3/4" />
        </div>
      ))}
    </div>
  );
}

// ─── Scripture reference highlighter ─────────────────────────────────────────
// Finds "Book chapter:verse" patterns inline and renders them bold crimson.

const SCRIPTURE_REF_RE = /\b((?:1\s|2\s|3\s)?[A-Z][a-z]+(?:\s[A-Z][a-z]+)?)\s+(\d+:\d+(?:-\d+)?)\b/g;

function renderWithRefs(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  SCRIPTURE_REF_RE.lastIndex = 0;
  while ((m = SCRIPTURE_REF_RE.exec(text)) !== null) {
    if (m.index > last) nodes.push(text.slice(last, m.index));
    nodes.push(
      <strong key={m.index} className="font-semibold text-crimson">
        {m[0]}
      </strong>
    );
    last = m.index + m[0].length;
  }
  if (last < text.length) nodes.push(text.slice(last));
  return nodes.length ? nodes : [text];
}

// ─── Verse Block ──────────────────────────────────────────────────────────────

interface VerseBlockProps {
  verse: CommentaryVerse;
  sourceName: string;
  reference: string;
  isTarget: boolean;
  targetRef: React.RefObject<HTMLDivElement>;
  onInsert: (text: string) => void;
}

function VerseBlock({ verse, sourceName, reference, isTarget, targetRef, onInsert }: VerseBlockProps) {
  const [inserted, setInserted] = useState(false);
  const [hasSelection, setHasSelection] = useState(false);
  const verseRef = useRef<HTMLDivElement>(null);
  const pendingTextRef = useRef<string>('');

  // Track whether user has selected text within this verse
  useEffect(() => {
    const handleSelectionChange = () => {
      const sel = window.getSelection();
      if (!sel || sel.isCollapsed || !verseRef.current) {
        setHasSelection(false);
        return;
      }
      const range = sel.getRangeAt(0);
      setHasSelection(
        verseRef.current.contains(range.commonAncestorContainer) && sel.toString().trim().length > 0
      );
    };
    document.addEventListener('selectionchange', handleSelectionChange);
    return () => document.removeEventListener('selectionchange', handleSelectionChange);
  }, []);

  const handleInsertMouseDown = () => {
    // Capture selection before the click event clears it
    const sel = window.getSelection();
    const selText = sel && !sel.isCollapsed && verseRef.current?.contains(sel.getRangeAt(0).commonAncestorContainer)
      ? sel.toString().trim()
      : '';
    // Always fall back to full joined text for insert
    pendingTextRef.current = selText || verse.text;
  };

  const handleInsert = () => {
    onInsert(pendingTextRef.current || verse.text);
    pendingTextRef.current = '';
    setInserted(true);
    setTimeout(() => setInserted(false), 1500);
  };

  return (
    <div
      ref={(el) => {
        (verseRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
        if (isTarget && targetRef) (targetRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
      }}
      className={`group relative mb-5 pb-5 border-b border-gray-100 dark:border-white/10 last:border-0 last:mb-0 last:pb-0 transition-colors duration-150 ${isTarget ? 'bg-gold/5 dark:bg-gold/10 -mx-4 px-4 py-2 rounded-lg' : ''}`}
    >
      {/* Verse number label */}
      <div className="mb-2">
        <span className="font-mono text-xs font-medium uppercase tracking-widest text-gold">
          v. {verse.verseNumber}
        </span>
      </div>

      {/* Commentary prose — render as separate paragraphs if structure is available */}
      <div className="space-y-3 select-text mb-3">
        {(verse.paragraphs ?? [verse.text]).map((para, i) => (
          <p key={i} className="text-sm leading-relaxed text-gray-700 dark:text-gray-300">
            {renderWithRefs(para)}
          </p>
        ))}
      </div>

      {/* Insert button — sticky to panel bottom while inside this verse block */}
      <div className="sticky bottom-0 bg-white dark:bg-[#12171E] pt-2 pb-1">
        <button
          onMouseDown={handleInsertMouseDown}
          onClick={handleInsert}
          className="flex items-center gap-1.5 px-2.5 py-1 text-xs rounded-md bg-blue-50 dark:bg-blue-900/30 text-blue-600 dark:text-blue-300 hover:bg-blue-100 dark:hover:bg-blue-800/40 transition-all duration-150"
          title={hasSelection ? 'Insert selected text as quote' : 'Insert full verse commentary as quote'}
        >
          {inserted ? (
            <span className="text-emerald-600 dark:text-emerald-400 font-medium">Inserted ✓</span>
          ) : (
            <>
              <Quote className="h-3 w-3" />
              {hasSelection ? 'Insert Selection' : 'Insert'}
            </>
          )}
        </button>
      </div>
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export function CommentaryLibrary({ isOpen, onClose, sermonScripture, onInsertQuote, embedded }: CommentaryLibraryProps) {
  const [passageInput, setPassageInput] = useState(sermonScripture || '');
  const [passageError, setPassageError] = useState('');
  const [parsedPassage, setParsedPassage] = useState<ParsedPassage | null>(
    () => parsePassage(sermonScripture || '')
  );
  const [activeSource, setActiveSource] = useState<CommentarySourceId>('henry');
  const [sourceDropdownOpen, setSourceDropdownOpen] = useState(false);
  const sourceDropdownRef = useRef<HTMLDivElement>(null);
  const targetVerseRef = useRef<HTMLDivElement>(null);
  const bodyRef = useRef<HTMLDivElement>(null);

  // Sync when sermon scripture changes
  useEffect(() => {
    if (sermonScripture) {
      setPassageInput(sermonScripture);
      const parsed = parsePassage(sermonScripture);
      if (parsed) {
        setParsedPassage(parsed);
        setPassageError('');
      } else {
        // Don't leave the panel blank with no explanation.
        setParsedPassage(null);
        setPassageError(`Couldn't read "${sermonScripture}" — try "Psalm 42" or "Romans 8:28"`);
      }
    }
  }, [sermonScripture]);

  // Scroll to target verse after data loads
  const handleScrollToVerse = () => {
    if (parsedPassage?.verse && targetVerseRef.current) {
      setTimeout(() => {
        targetVerseRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 150);
    }
  };

  // Filter sources — hide OT-only sources for NT books, NT-only sources for OT books
  const visibleSources = COMMENTARY_SOURCES.filter(s => {
    if (s.otOnly && parsedPassage && isNTBook(parsedPassage.bookId)) return false;
    if (s.ntOnly && parsedPassage && !isNTBook(parsedPassage.bookId)) return false;
    return true;
  });

  // Reset to first visible source if current becomes hidden
  useEffect(() => {
    const visible = visibleSources.map(s => s.id);
    if (!visible.includes(activeSource)) {
      setActiveSource(visible[0] as CommentarySourceId);
    }
  }, [parsedPassage?.bookId]);

  // Close source dropdown on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (sourceDropdownRef.current && !sourceDropdownRef.current.contains(e.target as Node)) {
        setSourceDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handlePassageSubmit = (e?: React.FormEvent) => {
    e?.preventDefault();
    const parsed = parsePassage(passageInput);
    if (parsed) {
      setParsedPassage(parsed);
      setPassageError('');
      // Scroll body to top when passage changes
      if (bodyRef.current) bodyRef.current.scrollTop = 0;
    } else {
      setPassageError('Try "John 3" or "Romans 8:28"');
    }
  };

  const { data, loading, error, refetch } = useCommentary({
    passage: parsedPassage,
    sourceId: activeSource,
  });

  // Scroll to target verse when data arrives
  useEffect(() => {
    if (data) handleScrollToVerse();
  }, [data]);

  const activeSourceMeta = COMMENTARY_SOURCES.find(s => s.id === activeSource)!;
  const displayReference = parsedPassage
    ? `${parsedPassage.bookName} ${parsedPassage.chapter}${parsedPassage.verse ? `:${parsedPassage.verse}` : ''}`
    : '';

  // Chapter navigation
  const canGoPrev = parsedPassage && parsedPassage.chapter > 1;
  const navigateChapter = (delta: number) => {
    if (!parsedPassage) return;
    const newChapter = parsedPassage.chapter + delta;
    if (newChapter < 1) return;
    const newPassage = { ...parsedPassage, chapter: newChapter, verse: null };
    setParsedPassage(newPassage);
    setPassageInput(`${newPassage.bookName} ${newPassage.chapter}`);
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
  };

  return (
    <>
      {/* Backdrop — only on mobile, not in embedded mode */}
      {!embedded && isOpen && (
        <div
          className="fixed inset-0 bg-black/20 z-[199] md:hidden"
          onClick={onClose}
        />
      )}

      {/* Panel */}
      <div
        className={embedded ? 'flex flex-col h-full bg-white dark:bg-[#12171E]' : `
          fixed right-0 top-0 bottom-0 z-[200]
          w-full md:w-[420px] bg-white dark:bg-[#12171E]
          border-l border-gray-200 dark:border-white/10
          shadow-2xl flex flex-col
          transition-transform duration-300 ease-in-out
          ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        `}
      >
        {/* ── Header ── */}
        <div className="flex-shrink-0 px-5 pt-5 pb-4 border-b border-gray-100 dark:border-white/10">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-2">
              <div className="w-7 h-7 rounded-lg bg-gold/20 flex items-center justify-center">
                <BookOpen className="h-4 w-4 text-gold" />
              </div>
              <h2 className="text-base font-semibold text-gray-900 dark:text-white">
                Commentary Library
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-1 text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors"
              aria-label="Close commentary panel"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Passage input */}
          <form onSubmit={handlePassageSubmit} className="flex gap-2">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-gray-400" />
              <input
                type="text"
                value={passageInput}
                onChange={e => { setPassageInput(e.target.value); setPassageError(''); }}
                onKeyDown={e => e.key === 'Enter' && handlePassageSubmit()}
                placeholder="e.g. John 3 or Romans 8:28"
                className="w-full pl-9 pr-3 py-2 text-sm bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-gold/50 focus:border-gold/50"
              />
            </div>
            <button
              type="submit"
              className="px-3 py-2 text-sm font-medium bg-gold text-white rounded-lg hover:bg-gold/90 transition-colors"
            >
              Go
            </button>
          </form>
          {passageError && (
            <p className="mt-1.5 text-xs text-red-500 dark:text-red-400">{passageError}</p>
          )}

          {/* Chapter nav */}
          {parsedPassage && (
            <div className="flex items-center justify-between mt-3">
              <button
                onClick={() => navigateChapter(-1)}
                disabled={!canGoPrev}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
              >
                <ChevronLeft className="h-3.5 w-3.5" />
                Prev chapter
              </button>
              <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
                {displayReference}
              </span>
              <button
                onClick={() => navigateChapter(1)}
                className="flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 transition-colors"
              >
                Next chapter
                <ChevronRight className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {/* ── Source Dropdown ── */}
        <div className="flex-shrink-0 px-5 py-3 border-b border-gray-100 dark:border-white/10">
          <div ref={sourceDropdownRef} className="relative">
            <button
              onClick={() => setSourceDropdownOpen(o => !o)}
              className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium bg-gray-50 dark:bg-white/5 border border-gray-200 dark:border-white/10 rounded-lg text-gray-900 dark:text-white hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
            >
              <span>{activeSourceMeta.label}</span>
              <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform duration-150 ${sourceDropdownOpen ? 'rotate-180' : ''}`} />
            </button>
            {sourceDropdownOpen && (
              <div className="absolute left-0 right-0 top-full mt-1 z-10 bg-white dark:bg-[#1a2230] border border-gray-200 dark:border-white/10 rounded-lg shadow-lg overflow-hidden">
                {visibleSources.map(source => (
                  <button
                    key={source.id}
                    onClick={() => {
                      setActiveSource(source.id);
                      setSourceDropdownOpen(false);
                      if (bodyRef.current) bodyRef.current.scrollTop = 0;
                    }}
                    className={`w-full flex items-center justify-between px-4 py-2.5 text-sm transition-colors ${
                      activeSource === source.id
                        ? 'bg-gold/10 text-gold font-medium'
                        : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-white/5'
                    }`}
                  >
                    <span>{source.label}</span>
                    {activeSource === source.id && (
                      <span className="w-1.5 h-1.5 rounded-full bg-gold" />
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* ── Body ── */}
        <div ref={bodyRef} className="flex-1 overflow-y-auto">
          {!parsedPassage ? (
            <div className="flex flex-col items-center justify-center h-full text-center px-8 py-12">
              <div className="w-12 h-12 rounded-full bg-gold/10 flex items-center justify-center mb-4">
                <BookOpen className="h-6 w-6 text-gold" />
              </div>
              <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Enter a passage to begin
              </p>
              <p className="text-xs text-gray-400 dark:text-gray-500">
                Try "John 3", "Psalm 23", or "Romans 8:28"
              </p>
            </div>
          ) : loading ? (
            <SkeletonLoader />
          ) : error ? (
            <div className="flex flex-col items-center justify-center px-8 py-12 text-center">
              <AlertCircle className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400 mb-4">{error}</p>
              <button
                onClick={refetch}
                className="flex items-center gap-2 px-3 py-1.5 text-xs font-medium text-blue-600 dark:text-blue-400 border border-blue-200 dark:border-blue-800 rounded-lg hover:bg-blue-50 dark:hover:bg-blue-900/20 transition-colors"
              >
                <RefreshCw className="h-3 w-3" />
                Try again
              </button>
            </div>
          ) : data && data.verses.length > 0 ? (
            <div className="px-5 py-5">
              {/* Source header */}
              <div className="flex items-center justify-between mb-5 pb-4 border-b border-gray-100 dark:border-white/10">
                <div>
                  <p className="text-xs font-semibold text-gray-900 dark:text-white">
                    {activeSourceMeta.label}
                  </p>
                  <p className="text-xs text-gray-400 dark:text-gray-500 mt-0.5">
                    {data.book} {data.chapter} • {data.verses.length} verses
                  </p>
                </div>
                <button
                  onClick={refetch}
                  className="w-7 h-7 flex items-center justify-center rounded-md text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 hover:bg-gray-100 dark:hover:bg-white/10 transition-colors"
                  title="Refresh"
                >
                  <RefreshCw className="h-3.5 w-3.5" />
                </button>
              </div>

              {/* Verses */}
              {data.verses.map(verse => (
                <VerseBlock
                  key={verse.verseNumber}
                  verse={verse}
                  sourceName={activeSourceMeta.label}
                  reference={displayReference}
                  isTarget={parsedPassage?.verse === verse.verseNumber}
                  targetRef={targetVerseRef}
                  onInsert={(text) => onInsertQuote(text, activeSourceMeta.label, displayReference)}
                />
              ))}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center px-8 py-12 text-center">
              <BookOpen className="h-8 w-8 text-gray-300 dark:text-gray-600 mb-3" />
              <p className="text-sm text-gray-500 dark:text-gray-400">
                No commentary found for this passage.
              </p>
            </div>
          )}
        </div>

        {/* ── Guzik Attribution Footer ── */}
        {activeSource === 'guzik' && data && (
          <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 dark:border-white/10 bg-gray-50/80 dark:bg-white/3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                © David Guzik — Enduring Word
              </p>
              <a
                href={`https://enduringword.com/bible-commentary/${data.book.toLowerCase().replace(/\s+/g, '-')}-${data.chapter}/`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 transition-colors"
              >
                View on site
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        {/* ── Chuck Smith Attribution Footer ── */}
        {activeSource === 'smith' && data && (
          <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 dark:border-white/10 bg-gray-50/80 dark:bg-white/3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                © Chuck Smith — C2000 Series
              </p>
              <a
                href={`https://www.blueletterbible.org/commentaries/smith_chuck/`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 transition-colors"
              >
                View on BLB
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        {/* ── Robertson Attribution Footer ── */}
        {activeSource === 'robertson' && data && (
          <div className="flex-shrink-0 px-5 py-3 border-t border-gray-100 dark:border-white/10 bg-gray-50/80 dark:bg-white/3">
            <div className="flex items-center justify-between">
              <p className="text-xs text-gray-400 dark:text-gray-500">
                A.T. Robertson — Word Pictures in the NT • Public domain
              </p>
              <a
                href={`https://www.studylight.org/commentaries/rwp/${data.book.toLowerCase().replace(/\s+/g, '-')}-${data.chapter}.html`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1 text-xs text-blue-500 hover:text-blue-600 dark:text-blue-400 transition-colors"
              >
                View on StudyLight
                <ExternalLink className="h-3 w-3" />
              </a>
            </div>
          </div>
        )}

        {/* ── Other Sources Attribution ── */}
        {activeSource !== 'guzik' && activeSource !== 'smith' && activeSource !== 'robertson' && data && (
          <div className="flex-shrink-0 px-5 py-2.5 border-t border-gray-100 dark:border-white/10 bg-gray-50/80 dark:bg-white/3">
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {activeSourceMeta.label} • Public domain • via bible.helloao.org
            </p>
          </div>
        )}
      </div>
    </>
  );
}
